/**
 * Admin Organization Hierarchy
 *
 * Dedicated page for managing the organizational hierarchy structure.
 * Groups can be placed at org units to define where their permissions apply.
 */

/* eslint-disable @typescript-eslint/no-explicit-any, react-hooks/exhaustive-deps, @typescript-eslint/no-unused-vars --
   Pre-existing debt across this ~1,700-line file (untyped Supabase row casts,
   mount-only fetch effects, an intentionally-unused allUnits prop) predates
   the retire/restore reversal work here. Fixing it is a behavior-risk sweep
   out of scope for that change; it is suppressed here, rather than swept, so
   the changed-files lint gate stays meaningful for new issues. */

import { OwnPlanInForceSetting } from '../../../components/plan-of-record/OwnPlanInForceSetting';
import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  Building2,
  Users,
  User,
  Plus,
  X,
  ChevronRight,
  ChevronDown,
  Trash2,
  Archive,
  Edit2,
  Shield,
  Layers,
  RefreshCw,
} from 'lucide-react';
import { StructureHistoryPanel } from '../../../components/hierarchy/StructureHistoryPanel';
import { StructureReorgWorkspace } from '../../../components/hierarchy/StructureReorgWorkspace';
import { useStructureReorgEnabled } from '../../../features/hierarchy/hooks/useStructureReorgEnabled';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { getSupabaseClient } from '../../../lib/supabaseClient';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { resolveAdminScope } from '../../../lib/adminScope';
import type { OrgUnit, OrgUnitType } from '../../../types/prioritization/enterpriseIdentity';
import { InlineGroupCreator } from './_InlineGroupCreator';
import { ReverseActionButton } from '../../../components/reversals/ReverseActionButton';
import { restoreOrgUnit } from '../../../lib/reversals/reversalsService';

// ============================================================================
// Main Component
// ============================================================================

export default function AdminOrgHierarchy() {
  // Select raw state to avoid infinite loops - use useMemo for derived data
  const orgUnitsMap = useEnterpriseIdentityStore((state) => state.orgUnits);
  const addOrgUnit = useEnterpriseIdentityStore((state) => state.addOrgUnit);
  const updateOrgUnit = useEnterpriseIdentityStore((state) => state.updateOrgUnit);
  const removeOrgUnit = useEnterpriseIdentityStore((state) => state.removeOrgUnit);
  const loadDemoData = useEnterpriseIdentityStore((state) => state.loadDemoData);
  const fetchFromSupabase = useEnterpriseIdentityStore((state) => state.fetchFromSupabase);
  
  const currentRole = useUserStore((state) => state.currentRole);
  const assignedRoleNames = useUserStore((state) => state.assignedRoleNames);
  const { currentTenantId, currentWorkspaceId, getWorkspacesForTenant } = useTenantStore();
  const adminScope = useMemo(
    () => resolveAdminScope(currentRole, assignedRoleNames),
    [currentRole, assignedRoleNames],
  );
  
  const structureReorgEnabled = useStructureReorgEnabled();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState<OrgUnit | null>(null);
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [reloading, setReloading] = useState(false);
  
  // Build scope so we only load org units for the current tenant/workspace (not other organizations)
  const fetchScope = useMemo(() => {
    if (adminScope === 'platform') return undefined; // platform sees all
    if (adminScope === 'workspace' && currentWorkspaceId) return { workspaceId: currentWorkspaceId };
    if (adminScope === 'tenant' && currentTenantId) {
      const workspaces = getWorkspacesForTenant(currentTenantId);
      const workspaceIds = workspaces.map((w: { id: string }) => w.id).filter(Boolean);
      // Pass workspaceIds even when empty so store returns empty list (no leak of other orgs)
      return { workspaceIds };
    }
    return undefined;
  }, [adminScope, currentTenantId, currentWorkspaceId, getWorkspacesForTenant]);
  
  // Fetch from Supabase on mount and when scope changes (tenant/workspace switcher)
  useEffect(() => {
    fetchFromSupabase(fetchScope);
  }, [fetchFromSupabase, fetchScope]);
  
  // Derive arrays from raw Map state using useMemo to prevent infinite loops.
  // When workspace-scoped, filter strictly to the current workspace so legacy
  // null-workspace nodes from other organisations don't bleed through.
  const orgUnits = useMemo(() => {
    const all = Array.from(orgUnitsMap.values());
    if (fetchScope?.workspaceId) {
      return all.filter(u => !u.workspaceId || u.workspaceId === fetchScope.workspaceId);
    }
    return all;
  }, [orgUnitsMap, fetchScope]);

  // Build tree structure.
  // Treat orphaned nodes (parent doesn't exist in the current set) as roots so
  // they are still visible and can be corrected — e.g. after a self-referential
  // parentId was accidentally saved.
  const rootUnits = useMemo(() => {
    const idSet = new Set(orgUnits.map(u => u.id));
    return orgUnits
      .filter(ou => !ou.parentId || !idSet.has(ou.parentId))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [orgUnits]);
  
  // Changes whenever something a structure version records changes, so the
  // history panel reloads after an edit made on this page.
  const structureSignature = useMemo(
    () => orgUnits.map(u => `${u.id}:${u.parentId ?? ''}:${u.displayName}:${u.active ? 1 : 0}`).sort().join('|'),
    [orgUnits],
  );

  const handleAddUnit = (data: Partial<OrgUnit>) => {
    let depth = 0;
    let parentId = data.parentId;

    if (!parentId) {
      const existingRoot = orgUnits.find(
        (u) => u.active && u.depth === 0 && !u.parentId && u.name !== 'workspace-queue',
      );
      if (existingRoot) {
        const useAsChild = confirm(
          `"${existingRoot.displayName}" is already the organization root. Add "${data.displayName}" as a child of that root instead?`,
        );
        if (!useAsChild) return;
        parentId = existingRoot.id;
      }
    }

    if (parentId) {
      const parent = orgUnits.find(u => u.id === parentId);
      if (parent) {
        depth = parent.depth + 1;
      }
    }
    
    addOrgUnit({
      name: data.name || data.displayName?.toLowerCase().replace(/\s+/g, '-') || '',
      displayName: data.displayName || '',
      type: data.type || 'team',
      parentId,
      path: [],
      depth,
      description: data.description,
      active: true,
    });
    setShowAddModal(false);
  };
  
  // `active` never rides the plain updateOrgUnit write — that bypassed the
  // retire RPC's active-children check entirely (a raw column write). A
  // change to `active` here always goes through the governed path instead:
  // unchecking calls the same retire action the tree/detail Retire buttons
  // use, checking calls the restoreOrgUnit reversal (which requires a reason
  // and can be refused, e.g. once the unit's parent is itself retired).
  const handleUpdateUnit = async (id: string, data: Partial<OrgUnit>) => {
    const existing = orgUnits.find(u => u.id === id);
    const { active: nextActive, ...rest } = data;
    const activeChanged = existing != null && typeof nextActive === 'boolean' && nextActive !== existing.active;

    if (activeChanged && existing) {
      if (nextActive === false) {
        await handleDeleteUnit(id);
      } else {
        const reason = window.prompt(`Why are you restoring "${existing.displayName}"?`);
        if (!reason || !reason.trim()) {
          setEditingUnit(null);
          return;
        }
        const result = await restoreOrgUnit(id, reason.trim());
        if (!result.ok) {
          alert(
            result.blockedBy
              ? `Can't undo this: ${result.blockedBy}${result.remedy ? ` — ${result.remedy}` : ''}`
              : result.error ?? 'Could not restore this org unit.',
          );
          return;
        }
        fetchFromSupabase(fetchScope);
      }
    }

    if (Object.keys(rest).length > 0) {
      const result = await updateOrgUnit(id, rest);
      if (!result.ok) {
        // The store has already rolled the local change back; keep the edit
        // form open so nothing the user typed is lost.
        alert(`Could not save this org unit: ${result.error ?? 'the server refused the change.'}`);
        return;
      }
    }
    setEditingUnit(null);
  };

  const handleDeleteUnit = async (id: string) => {
    // Org units are retired, never deleted: the unit keeps its id, its name
    // and everything that points at it, so past decisions stay readable. The
    // server enforces the same child rule (retire_structure_node, mig 727).
    const hasActiveChildren = orgUnits.some(u => u.parentId === id && u.active);
    if (hasActiveChildren) {
      alert('This unit still has active units under it. Retire or move them first.');
      return;
    }

    // window.prompt returns null on Cancel; an empty string retires with no
    // reason (the RPC's p_reason is optional and recorded as retired_reason).
    const reason = window.prompt(
      'Retire this organizational unit? It leaves pickers but stays on the record.\n\nWhy is it being retired? (optional)',
      '',
    );
    if (reason === null) return;
    const result = await removeOrgUnit(id, reason);
    if (!result.ok) {
      alert(`Could not retire this org unit: ${result.error ?? 'the server refused the change.'}`);
      return;
    }
    setSelectedUnitId(null);
  };
  
  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Building2 className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Organization Hierarchy"
        subtitle="Define your organizational structure and manage where groups have permissions"
      />

      {/* Actions Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Org Unit
          </button>
          
          {orgUnits.length === 0 && (
            <button
              onClick={loadDemoData}
              className="btn btn-secondary flex items-center gap-2"
            >
              <Layers className="h-4 w-4" />
              Load Demo Data
            </button>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={async () => {
              setReloading(true);
              await fetchFromSupabase(fetchScope);
              setReloading(false);
            }}
            disabled={reloading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] hover:bg-[var(--hover-tint)] transition-colors disabled:opacity-50"
            title="Reload from cloud"
          >
            <RefreshCw className={`h-4 w-4 ${reloading ? 'animate-spin' : ''}`} />
            Reload
          </button>
          <div className="text-sm text-[var(--text-ter)]">
            {orgUnits.length} unit{orgUnits.length !== 1 ? 's' : ''} defined
          </div>
        </div>
      </div>
      
      {/* Info Card */}
      <div className="card bg-[var(--slate-dim)] border-[var(--slate)]">
        <div className="flex items-start gap-3">
          <Building2 className="h-5 w-5 text-[var(--text-ter)] mt-0.5" />
          <div>
            <h3 className="font-medium text-[var(--text-sec)]">Organization Hierarchy</h3>
            <p className="text-sm text-[var(--text-sec)] mt-1">
              Define your organizational structure here. Groups can be placed at org units to control
              where their permissions apply. This enables department-level and team-level access control.
            </p>
          </div>
        </div>
      </div>
      
      {orgUnits.length === 0 ? (
        <div className="card text-center py-12">
          <Building2 className="h-12 w-12 text-[var(--text-ter)] mx-auto mb-4" />
          <p className="text-[var(--text-ter)] mb-2">No organizational units defined</p>
          <p className="text-sm text-[var(--text-ter)] mb-4">
            Start by adding your organization, then create divisions, departments, and teams
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tree View */}
          <div className="lg:col-span-2 card">
            <h3 className="font-semibold text-[var(--text-pri)] mb-4">Organization Hierarchy</h3>
            <div className="space-y-1">
              {rootUnits.map(ou => (
                <OrgUnitTreeItem 
                  key={ou.id} 
                  orgUnit={ou} 
                  allUnits={orgUnits} 
                  depth={0}
                  selectedId={selectedUnitId}
                  onSelect={setSelectedUnitId}
                  onEdit={setEditingUnit}
                  onDelete={(unitId) => void handleDeleteUnit(unitId)}
                  onAddChild={(parentId) => {
                    setSelectedUnitId(parentId);
                    setShowAddModal(true);
                  }}
                />
              ))}
            </div>
          </div>
          
          {/* Details Panel */}
          <div className="card">
            <h3 className="font-semibold text-[var(--text-pri)] mb-4">Details</h3>
            {selectedUnitId ? (
              <OrgUnitDetails
                unit={orgUnits.find(u => u.id === selectedUnitId)!}
                allUnits={orgUnits}
                onEdit={setEditingUnit}
                onDelete={(unitId) => void handleDeleteUnit(unitId)}
                onRestored={() => fetchFromSupabase(fetchScope)}
              />
            ) : (
              <p className="text-sm text-[var(--text-ter)]">Select an org unit to view details</p>
            )}
          </div>
        </div>
      )}
      
      {structureReorgEnabled && (
        <StructureReorgWorkspace
          workspaceId={currentWorkspaceId}
          kind="org"
          onLiveStructureChanged={() => void fetchFromSupabase(fetchScope)}
        />
      )}

      {/* History is per workspace, whatever scope the chart above is listing. */}
      <StructureHistoryPanel workspaceId={currentWorkspaceId} kind="org" refreshKey={structureSignature} onReverted={() => fetchFromSupabase(fetchScope)} />

      {/* Add/Edit Modal */}
      {(showAddModal || editingUnit) && (
        <OrgUnitModal
          unit={editingUnit}
          parentId={selectedUnitId}
          allUnits={orgUnits}
          onClose={() => {
            setShowAddModal(false);
            setEditingUnit(null);
          }}
          onSave={(data) => {
            if (editingUnit) {
              handleUpdateUnit(editingUnit.id, data);
            } else {
              handleAddUnit(data);
            }
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Org Unit Tree Item
// ============================================================================

function OrgUnitTreeItem({
  orgUnit,
  allUnits,
  depth,
  selectedId,
  onSelect,
  onEdit,
  onDelete,
  onAddChild,
}: {
  orgUnit: OrgUnit;
  allUnits: OrgUnit[];
  depth: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onEdit: (unit: OrgUnit) => void;
  onDelete: (id: string) => void;
  onAddChild: (parentId: string) => void;
}) {
  const [expanded, setExpanded] = useState(depth < 2);
  const children = allUnits.filter(ou => ou.parentId === orgUnit.id)
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
  const hasChildren = children.length > 0;
  const isSelected = selectedId === orgUnit.id;
  // Orphaned: has a parentId but the parent doesn't exist in the current set.
  const allUnitIds = new Set(allUnits.map(u => u.id));
  const isOrphaned = !!orgUnit.parentId && !allUnitIds.has(orgUnit.parentId);

  const typeBadgeClass = 'bg-[var(--hover-tint)] text-[var(--text-ter)]';
  
  return (
    <div>
      {isOrphaned && (
        <div className="mx-2 mb-1 px-2 py-1 text-xs text-[var(--mustard)] bg-[var(--mustard-dim)] border border-[var(--mustard)] rounded flex items-center gap-1.5" style={{ marginLeft: `${depth * 20 + 8}px` }}>
          <span>⚠ Parent node missing — edit this unit to clear or reassign its parent.</span>
        </div>
      )}
      <div
        className={`group flex items-center gap-2 p-2  cursor-pointer transition-colors ${
          isSelected ? 'bg-primary-50 border border-primary-200' : 'hover:bg-[var(--surface)]'
        }`}
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
        onClick={() => onSelect(orgUnit.id)}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) setExpanded(!expanded);
          }}
          className="p-0.5 hover:bg-[var(--hover-tint)] rounded"
        >
          {hasChildren ? (
            expanded ? (
              <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" />
            ) : (
              <ChevronRight className="h-4 w-4 text-[var(--text-ter)]" />
            )
          ) : (
            <div className="w-4 h-4" />
          )}
        </button>
        
        <Building2 className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
        
        <span className="font-medium text-[var(--text-pri)] truncate">{orgUnit.displayName}</span>
        
        {orgUnit.type && (
          <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${typeBadgeClass}`}>
            {orgUnit.type}
          </span>
        )}
        
        {!orgUnit.active && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--coral-dim)] text-[var(--coral)] flex-shrink-0">
            Inactive
          </span>
        )}
        
        {/* Hover Actions */}
        <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddChild(orgUnit.id);
            }}
            className="p-1 text-[var(--text-ter)] hover:text-primary-600 hover:bg-primary-50 rounded"
            title="Add child unit"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(orgUnit);
            }}
            className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] rounded"
            title="Edit"
          >
            <Edit2 className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(orgUnit.id);
            }}
            className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] rounded"
            title="Retire"
          >
            <Archive className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      
      {expanded && hasChildren && (
        <div>
          {children.map(child => (
            <OrgUnitTreeItem
              key={child.id}
              orgUnit={child}
              allUnits={allUnits}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddChild={onAddChild}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Org Unit Details Panel
// ============================================================================

interface GroupPlacement {
  id: string;
  groupId: string;
  groupName: string;
  roles: string[];
  inheritable: boolean;
  inherited: boolean;
  inheritedFromName?: string;
}

interface UserPlacement {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  inheritable: boolean;
  inherited: boolean;
  inheritedFromName?: string;
}

function OrgUnitDetails({
  unit,
  allUnits,
  onEdit,
  onDelete,
  onRestored,
}: {
  unit: OrgUnit;
  allUnits: OrgUnit[];
  onEdit: (unit: OrgUnit) => void;
  onDelete: (id: string) => void;
  /** Refetch after a successful restore. */
  onRestored: () => void;
}) {
  const parent = unit.parentId ? allUnits.find(u => u.id === unit.parentId) : null;
  const children = allUnits.filter(u => u.parentId === unit.id);
  
  const [directPlacements, setDirectPlacements] = useState<GroupPlacement[]>([]);
  const [inheritedPlacements, setInheritedPlacements] = useState<GroupPlacement[]>([]);
  const [directUserPlacements, setDirectUserPlacements] = useState<UserPlacement[]>([]);
  const [inheritedUserPlacements, setInheritedUserPlacements] = useState<UserPlacement[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showAssignUserModal, setShowAssignUserModal] = useState(false);
  const [editingPlacement, setEditingPlacement] = useState<GroupPlacement | null>(null);
  
  const loadAssignments = async () => {
    setLoadingAssignments(true);
    try {
      const supabase = getSupabaseClient();
      
      const { data: directData, error: directError } = await supabase
        .from('group_node_assignments')
        .select('id, group_id, inheritable')
        .eq('org_unit_id', unit.id)
        .eq('active', true);
      
      if (directError) throw directError;
      
      const parentIds = unit.path.filter(p => p !== unit.id);
      let inheritedData: any[] = [];
      
      if (parentIds.length > 0) {
        const { data: inherited, error: inheritedError } = await supabase
          .from('group_node_assignments')
          .select('id, group_id, org_unit_id, inheritable')
          .in('org_unit_id', parentIds)
          .eq('active', true)
          .eq('inheritable', true);
        
        if (inheritedError) throw inheritedError;
        inheritedData = inherited || [];
      }
      
      const allGroupIds = [
        ...(directData || []).map((d: any) => d.group_id),
        ...inheritedData.map((d: any) => d.group_id),
      ];
      
      const { data: groupsData } = allGroupIds.length > 0 
        ? await supabase.from('groups').select('id, name').in('id', allGroupIds)
        : { data: [] };
      const groupNames = new Map<string, string>();
      (groupsData || []).forEach((g: any) => groupNames.set(g.id, g.name));
      
      const { data: roleAssignments } = allGroupIds.length > 0
        ? await supabase
            .from('role_assignments')
            .select('subject_id, roles:role_id(display_name, name)')
            .eq('subject_type', 'group')
            .in('subject_id', allGroupIds)
            .eq('active', true)
        : { data: [] };
      
      const groupRolesMap = new Map<string, string[]>();
      (roleAssignments || []).forEach((ra: any) => {
        const roleName = ra.roles?.display_name || ra.roles?.name || 'Unknown';
        const existing = groupRolesMap.get(ra.subject_id) || [];
        if (!existing.includes(roleName)) {
          groupRolesMap.set(ra.subject_id, [...existing, roleName]);
        }
      });
      
      const orgUnitNames = new Map<string, string>();
      allUnits.forEach(ou => orgUnitNames.set(ou.id, ou.displayName));
      
      setDirectPlacements((directData || []).map((d: any) => ({
        id: d.id,
        groupId: d.group_id,
        groupName: groupNames.get(d.group_id) || 'Unknown Group',
        roles: groupRolesMap.get(d.group_id) || [],
        inheritable: d.inheritable ?? true,
        inherited: false,
      })));
      
      setInheritedPlacements(inheritedData.map((d: any) => ({
        id: d.id,
        groupId: d.group_id,
        groupName: groupNames.get(d.group_id) || 'Unknown Group',
        roles: groupRolesMap.get(d.group_id) || [],
        inheritable: true,
        inherited: true,
        inheritedFromName: orgUnitNames.get(d.org_unit_id) || 'Parent',
      })));

      // Load user placements
      const { data: directUsers } = await supabase
        .from('user_node_assignments')
        .select('id, user_id, inheritable')
        .eq('org_unit_id', unit.id)
        .eq('active', true);

      let inheritedUsers: any[] = [];
      if (parentIds.length > 0) {
        const { data: inhUsers } = await supabase
          .from('user_node_assignments')
          .select('id, user_id, org_unit_id, inheritable')
          .in('org_unit_id', parentIds)
          .eq('active', true)
          .eq('inheritable', true);
        inheritedUsers = inhUsers || [];
      }

      const allUserIds = [
        ...(directUsers || []).map((d: any) => d.user_id),
        ...inheritedUsers.map((d: any) => d.user_id),
      ];

      const userNameMap = new Map<string, { name: string; email: string }>();
      if (allUserIds.length > 0) {
        const { data: usersData } = await supabase.from('users').select('id, name, email').in('id', allUserIds);
        (usersData || []).forEach((u: any) => userNameMap.set(u.id, { name: u.name || '', email: u.email || '' }));
      }

      setDirectUserPlacements((directUsers || []).map((d: any) => ({
        id: d.id,
        userId: d.user_id,
        userName: userNameMap.get(d.user_id)?.name || '',
        userEmail: userNameMap.get(d.user_id)?.email || d.user_id,
        inheritable: d.inheritable ?? false,
        inherited: false,
      })));

      setInheritedUserPlacements(inheritedUsers.map((d: any) => ({
        id: d.id,
        userId: d.user_id,
        userName: userNameMap.get(d.user_id)?.name || '',
        userEmail: userNameMap.get(d.user_id)?.email || d.user_id,
        inheritable: true,
        inherited: true,
        inheritedFromName: orgUnitNames.get(d.org_unit_id) || 'Parent',
      })));
    } catch (err) {
      console.error('Failed to load placements:', err);
    } finally {
      setLoadingAssignments(false);
    }
  };
  
  useEffect(() => {
    loadAssignments();
  }, [unit.id]);
  
  const handleRemovePlacement = async (placementId: string) => {
    if (!confirm('Remove this group from this node?')) return;
    try {
      const supabase = getSupabaseClient();
      // supabase-js RESOLVES with { error } rather than throwing, so this used
      // to await the delete, ignore the result and reload — a refusal (RLS, a
      // constraint) left the row exactly where it was and said nothing at all.
      // The catch below could never fire for the case it was written for.
      const { error } = await supabase.from('group_node_assignments').delete().eq('id', placementId);
      if (error) throw error;
      loadAssignments();
    } catch (err) {
      console.error('Failed to remove placement:', err);
      alert(`Could not remove this group from the node: ${err instanceof Error ? err.message : 'the database refused the change'}`);
    }
  };

  const handleRemoveUserPlacement = async (placementId: string) => {
    if (!confirm('Remove this person from this node?')) return;
    try {
      const supabase = getSupabaseClient();
      // Same silent failure as the group placement above: the delete's error
      // was never read, so a refused removal looked like nothing happened.
      const { error } = await supabase.from('user_node_assignments').delete().eq('id', placementId);
      if (error) throw error;
      loadAssignments();
    } catch (err) {
      console.error('Failed to remove user placement:', err);
      alert(`Could not remove this person from the node: ${err instanceof Error ? err.message : 'the database refused the change'}`);
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h4 className="font-semibold text-[var(--text-pri)]">{unit.displayName}</h4>
          <p className="text-sm text-[var(--text-ter)]">{unit.name}</p>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onEdit(unit)}
            className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] rounded"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          {unit.active ? (
            <button
              onClick={() => onDelete(unit.id)}
              className="p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] rounded"
              title="Retire"
            >
              <Archive className="h-4 w-4" />
            </button>
          ) : (
            <ReverseActionButton
              label="Restore org unit"
              confirmTitle="Restore org unit"
              explain={`This will bring "${unit.displayName}" back as an active organizational unit, reversing its retirement.`}
              run={(reason) => restoreOrgUnit(unit.id, reason)}
              onDone={onRestored}
              variant="ghost"
              size="sm"
              className="!px-2 !py-1.5 !text-xs"
            />
          )}
        </div>
      </div>

      <div className="space-y-3 text-sm">
        {unit.type && (
          <div>
            <span className="text-[var(--text-ter)]">Label:</span>
            <span className="ml-2">{unit.type}</span>
          </div>
        )}
        
        {parent && (
          <div>
            <span className="text-[var(--text-ter)]">Parent:</span>
            <span className="ml-2">{parent.displayName}</span>
          </div>
        )}
        
        {unit.description && (
          <div>
            <span className="text-[var(--text-ter)]">Description:</span>
            <p className="mt-1 text-[var(--text-sec)]">{unit.description}</p>
          </div>
        )}
        
        <div>
          <span className="text-[var(--text-ter)]">Status:</span>
          <span className={`ml-2 ${unit.active ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--coral)]'}`}>
            {unit.active ? 'Active' : 'Inactive'}
          </span>
        </div>

        <OwnPlanInForceSetting orgUnitId={unit.id} isTop={!unit.parentId} />
        
        {children.length > 0 && (
          <div>
            <span className="text-[var(--text-ter)]">Direct Children:</span>
            <span className="ml-2">{children.length} unit{children.length !== 1 ? 's' : ''}</span>
          </div>
        )}
        
        <div>
          <span className="text-[var(--text-ter)]">Hierarchy Depth:</span>
          <span className="ml-2">{unit.depth}</span>
        </div>
        
        {unit.syncSource && unit.syncSource !== 'local' && (
          <div>
            <span className="text-[var(--text-ter)]">Sync Source:</span>
            <span className="ml-2">{unit.syncSource}</span>
          </div>
        )}
      </div>
      
      {/* Groups Placed at This Org Unit */}
      <div className="pt-4 border-t">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-[var(--text-sec)] flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Groups at This Node
          </h4>
          <button
            onClick={() => setShowAssignModal(true)}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-[var(--slate-dim)] text-[var(--text-sec)] rounded hover:bg-[var(--slate-dim)]"
          >
            <Plus className="h-3 w-3" />
            Place Group
          </button>
        </div>
        
        {loadingAssignments ? (
          <p className="text-sm text-[var(--text-ter)]">Loading...</p>
        ) : directPlacements.length === 0 && inheritedPlacements.length === 0 ? (
          <p className="text-sm text-[var(--text-ter)] italic">No groups placed at this node</p>
        ) : (
          <div className="space-y-4">
            {directPlacements.length > 0 && (
              <div>
                <p className="text-xs text-[var(--text-ter)] mb-2">Placed directly at this node:</p>
                <div className="space-y-2">
                  {directPlacements.map(p => (
                    <div key={p.id} className="p-2 bg-[var(--surface)] text-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Users className="h-3 w-3 text-[var(--text-ter)]" />
                          <span className="font-medium text-[var(--text-pri)]">{p.groupName}</span>
                          {p.inheritable && (
                            <span className="text-xs bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] px-1.5 py-0.5 rounded">+ children</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setEditingPlacement(p)}
                            className="p-1 text-[var(--text-ter)] hover:text-[var(--text-ter)]"
                            title="Edit"
                          >
                            <Edit2 className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => handleRemovePlacement(p.id)}
                            className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)]"
                            title="Remove"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                      {p.roles.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {p.roles.map(role => (
                            <span key={role} className="text-xs bg-[var(--slate-dim)] text-[var(--text-sec)] px-1.5 py-0.5 rounded">
                              {role}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {inheritedPlacements.length > 0 && (
              <div>
                <p className="text-xs text-[var(--text-ter)] mb-2">Inherited from parent:</p>
                <div className="space-y-2">
                  {inheritedPlacements.map(p => (
                    <div key={p.id} className="p-2 bg-[var(--surface)]/50 text-sm border border-dashed border-[var(--rule)]">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Users className="h-3 w-3 text-[var(--text-ter)]" />
                          <span className="text-[var(--text-sec)]">{p.groupName}</span>
                        </div>
                        <span className="text-xs text-[var(--text-ter)] italic">from {p.inheritedFromName}</span>
                      </div>
                      {p.roles.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {p.roles.map(role => (
                            <span key={role} className="text-xs bg-[var(--slate-dim)] text-[var(--text-sec)] px-1.5 py-0.5 rounded">
                              {role}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Users Placed at This Org Unit */}
      <div className="pt-4 border-t">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-[var(--text-sec)] flex items-center gap-2">
            <User className="h-4 w-4" />
            Users at This Node
          </h4>
          <button
            onClick={() => setShowAssignUserModal(true)}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-[var(--slate-dim)] text-[var(--text-sec)] rounded hover:bg-[var(--slate-dim)]"
          >
            <Plus className="h-3 w-3" />
            Place User
          </button>
        </div>

        {loadingAssignments ? (
          <p className="text-sm text-[var(--text-ter)]">Loading...</p>
        ) : directUserPlacements.length === 0 && inheritedUserPlacements.length === 0 ? (
          <p className="text-sm text-[var(--text-ter)] italic">No users placed at this node</p>
        ) : (
          <div className="space-y-4">
            {directUserPlacements.length > 0 && (
              <div>
                <p className="text-xs text-[var(--text-ter)] mb-2">Placed directly at this node:</p>
                <div className="space-y-2">
                  {directUserPlacements.map(p => (
                    <div key={p.id} className="p-2 bg-[var(--surface)] text-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <User className="h-3 w-3 text-[var(--text-ter)]" />
                          <span className="font-medium text-[var(--text-pri)]">{p.userName || p.userEmail}</span>
                          {p.inheritable && (
                            <span className="text-xs bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] px-1.5 py-0.5 rounded">+ children</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleRemoveUserPlacement(p.id)}
                            className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)]"
                            title="Remove"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                      {p.userName && (
                        <p className="text-xs text-[var(--text-ter)] mt-1 ml-5">{p.userEmail}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {inheritedUserPlacements.length > 0 && (
              <div>
                <p className="text-xs text-[var(--text-ter)] mb-2">Inherited from parent:</p>
                <div className="space-y-2">
                  {inheritedUserPlacements.map(p => (
                    <div key={p.id} className="p-2 bg-[var(--surface)]/50 text-sm border border-dashed border-[var(--rule)]">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <User className="h-3 w-3 text-[var(--text-ter)]" />
                          <span className="text-[var(--text-sec)]">{p.userName || p.userEmail}</span>
                        </div>
                        <span className="text-xs text-[var(--text-ter)] italic">from {p.inheritedFromName}</span>
                      </div>
                      {p.userName && (
                        <p className="text-xs text-[var(--text-ter)] mt-1 ml-5">{p.userEmail}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {showAssignModal && (
        <AssignGroupToNodeModal
          unit={unit}
          allUnits={allUnits}
          onClose={() => setShowAssignModal(false)}
          onSave={() => {
            setShowAssignModal(false);
            loadAssignments();
          }}
        />
      )}

      {showAssignUserModal && (
        <AssignUserToNodeModal
          unit={unit}
          onClose={() => setShowAssignUserModal(false)}
          onSave={() => {
            setShowAssignUserModal(false);
            loadAssignments();
          }}
        />
      )}
      
      {editingPlacement && (
        <EditGroupPlacementModal
          placement={editingPlacement}
          onClose={() => setEditingPlacement(null)}
          onSave={() => {
            setEditingPlacement(null);
            loadAssignments();
          }}
        />
      )}
    </div>
  );
}

function AdminModalPortal({ children }: { children: ReactNode }) {
  if (typeof document === 'undefined') return null;
  return createPortal(children, document.body);
}

// ============================================================================
// Assign Group to Node Modal
// ============================================================================

interface GroupWithRoles {
  id: string;
  name: string;
  roles: string[];
}

function AssignGroupToNodeModal({
  unit,
  allUnits,
  onClose,
  onSave,
}: {
  unit: OrgUnit;
  allUnits: OrgUnit[];
  onClose: () => void;
  onSave: () => void;
}) {
  const { currentWorkspaceId, currentTenantId } = useTenantStore();
  const [groups, setGroups] = useState<GroupWithRoles[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [inheritable, setInheritable] = useState(true);
  
  const selectedGroup = groups.find(g => g.id === selectedGroupId);
  
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const supabase = getSupabaseClient();
        
        let groupsQuery = supabase.from('groups').select('id, name').order('name');
        if (currentWorkspaceId) {
          groupsQuery = groupsQuery.eq('workspace_id', currentWorkspaceId);
        } else {
          groupsQuery = groupsQuery.eq('workspace_id', '00000000-0000-0000-0000-000000000000');
        }
        const { data: groupsData } = await groupsQuery;
        
        let raQuery = supabase
          .from('role_assignments')
          .select('subject_id, roles:role_id(display_name, name)')
          .eq('subject_type', 'group')
          .eq('active', true);
        if (currentWorkspaceId && currentTenantId) {
          raQuery = raQuery.or(`scope_id.eq.${currentWorkspaceId},scope_id.eq.${currentTenantId}`);
        } else if (currentWorkspaceId) {
          raQuery = raQuery.eq('scope_id', currentWorkspaceId);
        } else if (currentTenantId) {
          raQuery = raQuery.eq('scope_id', currentTenantId);
        }
        const { data: roleAssignments } = await raQuery;
        
        const groupRolesMap = new Map<string, string[]>();
        (roleAssignments || []).forEach((ra: any) => {
          const roleName = ra.roles?.display_name || ra.roles?.name || 'Unknown Role';
          const existing = groupRolesMap.get(ra.subject_id) || [];
          if (!existing.includes(roleName)) {
            groupRolesMap.set(ra.subject_id, [...existing, roleName]);
          }
        });
        
        setGroups((groupsData || []).map(g => ({
          id: g.id,
          name: g.name,
          roles: groupRolesMap.get(g.id) || [],
        })));
      } catch (err) {
        console.error('Failed to load groups:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);
  
  const handleSave = async () => {
    if (!selectedGroupId) {
      alert('Please select a group');
      return;
    }
    
    setSaving(true);
    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.from('group_node_assignments').insert({
        group_id: selectedGroupId,
        org_unit_id: unit.id,
        inheritable: inheritable,
        active: true,
        granted_by: 'admin',
        grant_source: 'manual',
      });
      
      if (error) throw error;
      onSave();
    } catch (err) {
      console.error('Failed to assign group:', err);
      alert('Failed to assign group: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <AdminModalPortal>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] shadow-xl w-full max-w-md border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)] dark:border-[var(--rule)]">
          <h2 className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Place Group at {unit.displayName}</h2>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-sec)]">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-6">
        {loading ? (
          <p className="text-center py-8 text-[var(--text-ter)] dark:text-[var(--text-sec)]">Loading...</p>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">Group</label>
              <select
                value={selectedGroupId}
                onChange={(e) => setSelectedGroupId(e.target.value)}
                className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] text-[var(--text-pri)] dark:text-[var(--text-pri)] w-full border border-[var(--rule)] dark:border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a group...</option>
                {groups.map(g => (
                  <option key={g.id} value={g.id}>
                    {g.name} {g.roles.length > 0 ? `(${g.roles.join(', ')})` : '(no roles)'}
                  </option>
                ))}
              </select>
              <InlineGroupCreator
                workspaceId={currentWorkspaceId}
                onCreated={(created) => {
                  setGroups(prev => [...prev, { id: created.id, name: created.displayName, roles: [] }]);
                  setSelectedGroupId(created.id);
                }}
              />
            </div>
            
            {selectedGroup && (
              <div className="p-3 bg-[var(--surface)] dark:bg-[var(--bg-raised)]">
                <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">This group has the following roles:</p>
                {selectedGroup.roles.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {selectedGroup.roles.map(role => (
                      <span key={role} className="text-xs bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 text-[var(--text-sec)] px-2 py-0.5 rounded">
                        {role}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[var(--mustard)] dark:text-[var(--mustard)] italic">No roles assigned. Assign roles in Groups page first.</p>
                )}
              </div>
            )}
            
            <div className="pt-2">
              <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-2">Scope</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="inheritable"
                    checked={inheritable}
                    onChange={() => setInheritable(true)}
                    className="text-[var(--text-ter)]"
                  />
                  <span className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">This node and all children</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="inheritable"
                    checked={!inheritable}
                    onChange={() => setInheritable(false)}
                    className="text-[var(--text-ter)]"
                  />
                  <span className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">This node only</span>
                </label>
              </div>
            </div>
          </div>
        )}
        </div>
        
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[var(--text-sec)] dark:text-[var(--text-sec)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !selectedGroupId}
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Place Group'}
          </button>
        </div>
      </div>
    </div>
    </AdminModalPortal>
  );
}

// ============================================================================
// Assign User to Node Modal
// ============================================================================

function AssignUserToNodeModal({
  unit,
  onClose,
  onSave,
}: {
  unit: OrgUnit;
  onClose: () => void;
  onSave: () => void;
}) {
  const { currentWorkspaceId: modalWorkspaceId, currentTenantId: modalTenantId } = useTenantStore();
  const [users, setUsers] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [inheritable, setInheritable] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const supabase = getSupabaseClient();
        // Scope users to the current workspace via the RPC (excludes super-admins)
        let usersData: Array<{ id: string; name: string; email: string }> = [];
        if (modalWorkspaceId) {
          const { data: rpcData } = await supabase.rpc('get_workspace_participants', { p_workspace_id: modalWorkspaceId });
          usersData = (rpcData || []).map((u: Record<string, unknown>) => ({
            id: u.id as string,
            name: (u.name as string) || '',
            email: (u.email as string) || '',
          }));
        } else if (modalTenantId) {
          // Fall back to tenant-scoped users
          const { data: memberships } = await supabase.from('workspace_memberships')
            .select('user_id')
            .eq('status', 'active');
          const userIds = (memberships || []).map((m: { user_id: string }) => m.user_id);
          if (userIds.length > 0) {
            const { data: ud } = await supabase.from('users').select('id, name, email').in('id', userIds).order('name');
            usersData = (ud || []).map((u: { id: string; name: string; email: string }) => ({
              id: u.id,
              name: u.name || '',
              email: u.email || '',
            }));
          }
        }
        setUsers(usersData);
      } catch (err) {
        console.error('Failed to load users:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [modalWorkspaceId, modalTenantId]);

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(u =>
      u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    );
  }, [users, searchQuery]);

  const handleSave = async () => {
    if (!selectedUserId) {
      alert('Please select a user');
      return;
    }

    setSaving(true);
    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.from('user_node_assignments').insert({
        user_id: selectedUserId,
        org_unit_id: unit.id,
        inheritable,
        active: true,
        granted_by: 'admin',
        grant_source: 'manual',
      });

      if (error) throw error;
      onSave();
    } catch (err) {
      console.error('Failed to assign user:', err);
      alert('Failed to assign user: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const selectedUser = users.find(u => u.id === selectedUserId);

  return (
    <AdminModalPortal>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] shadow-xl w-full max-w-md border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)] dark:border-[var(--rule)]">
          <h2 className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Place User at {unit.displayName}</h2>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-sec)]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
        {loading ? (
          <p className="text-center py-8 text-[var(--text-ter)] dark:text-[var(--text-sec)]">Loading...</p>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">User</label>

              {/* Selected user chip */}
              {selectedUser ? (
                <div className="flex items-center justify-between gap-2 px-3 py-2 mb-2 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 border border-[var(--slate)] dark:border-[var(--slate)]">
                  <div className="flex items-center gap-2 min-w-0">
                    <User className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[var(--text-sec)] truncate">{selectedUser.name || selectedUser.email}</p>
                      {selectedUser.name && <p className="text-xs text-[var(--text-sec)] truncate">{selectedUser.email}</p>}
                    </div>
                  </div>
                  <button
                    onClick={() => { setSelectedUserId(''); setSearchQuery(''); }}
                    className="text-[var(--text-sec)] hover:text-[var(--text-pri)] flex-shrink-0"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  className="w-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 dark:placeholder-[var(--text-ter)] px-3 py-2 mb-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              )}

              {/* User list — only show when no user selected */}
              {!selectedUser && (
                <div className="max-h-48 overflow-y-auto border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]">
                  {filteredUsers.length === 0 ? (
                    <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] italic p-3">No users found</p>
                  ) : (
                    filteredUsers.map(u => (
                      <button
                        key={u.id}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault(); // prevent input blur before click registers
                          setSelectedUserId(u.id);
                          setSearchQuery('');
                        }}
                        className="w-full text-left px-3 py-2.5 text-sm hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 flex items-center gap-2 border-b border-[var(--rule)] dark:border-[var(--rule)] last:border-0"
                      >
                        <User className="h-3.5 w-3.5 text-[var(--text-ter)] dark:text-[var(--text-ter)] flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">{u.name || u.email}</p>
                          {u.name && <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] truncate">{u.email}</p>}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="pt-2">
              <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-2">Scope</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="user-inheritable"
                    checked={!inheritable}
                    onChange={() => setInheritable(false)}
                    className="text-[var(--text-ter)]"
                  />
                  <span className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">This node only</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="user-inheritable"
                    checked={inheritable}
                    onChange={() => setInheritable(true)}
                    className="text-[var(--text-ter)]"
                  />
                  <span className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">This node and all children</span>
                </label>
              </div>
            </div>

            <div className="p-3 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border border-[var(--mustard)] dark:border-[var(--mustard)]">
              <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
                Placing individual users is supported, but placing <strong>groups</strong> is the recommended best practice for scalable access management.
              </p>
            </div>
          </div>
        )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[var(--text-sec)] dark:text-[var(--text-sec)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !selectedUserId}
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Place User'}
          </button>
        </div>
      </div>
    </div>
    </AdminModalPortal>
  );
}

// ============================================================================
// Edit Group Placement Modal
// ============================================================================

function EditGroupPlacementModal({
  placement,
  onClose,
  onSave,
}: {
  placement: GroupPlacement;
  onClose: () => void;
  onSave: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [inheritable, setInheritable] = useState(placement.inheritable);
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase
        .from('group_node_assignments')
        .update({
          inheritable: inheritable,
          updated_at: new Date().toISOString(),
        })
        .eq('id', placement.id);
      
      if (error) throw error;
      onSave();
    } catch (err) {
      console.error('Failed to update placement:', err);
      alert('Failed to update: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <AdminModalPortal>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] shadow-xl w-full max-w-md border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)] dark:border-[var(--rule)]">
          <h2 className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Edit Group Placement</h2>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-sec)]">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-6">
        <div className="mb-4 p-3 bg-[var(--surface)] dark:bg-[var(--bg-raised)]">
          <div className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">Group</div>
          <div className="font-medium dark:text-[var(--text-pri)]">{placement.groupName}</div>
          {placement.roles.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {placement.roles.map(role => (
                <span key={role} className="text-xs bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 text-[var(--text-sec)] px-1.5 py-0.5 rounded">
                  {role}
                </span>
              ))}
            </div>
          )}
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-2">Scope</label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="inheritable"
                  checked={inheritable}
                  onChange={() => setInheritable(true)}
                  className="text-[var(--text-ter)]"
                />
                <span className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">This node and all children</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="inheritable"
                  checked={!inheritable}
                  onChange={() => setInheritable(false)}
                  className="text-[var(--text-ter)]"
                />
                <span className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">This node only</span>
              </label>
            </div>
          </div>
        </div>
        </div>
        
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[var(--text-sec)] dark:text-[var(--text-sec)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
    </AdminModalPortal>
  );
}

// ============================================================================
// Org Unit Add/Edit Modal
// ============================================================================

function OrgUnitModal({
  unit,
  parentId,
  allUnits,
  onClose,
  onSave,
}: {
  unit: OrgUnit | null;
  parentId: string | null;
  allUnits: OrgUnit[];
  onClose: () => void;
  onSave: (data: Partial<OrgUnit>) => void;
}) {
  const [formData, setFormData] = useState<Partial<OrgUnit>>({
    displayName: unit?.displayName || '',
    name: unit?.name || '',
    type: unit?.type || 'team',
    // When editing, use the unit's own parentId (null becomes undefined = root).
    // Only fall back to the parentId prop when adding a new unit.
    parentId: unit != null ? (unit.parentId ?? undefined) : (parentId ?? undefined),
    description: unit?.description || '',
    active: unit?.active ?? true,
  });
  
  const isEditing = !!unit;
  
  const handleDisplayNameChange = (displayName: string) => {
    setFormData(prev => ({
      ...prev,
      displayName,
      name: isEditing ? prev.name : displayName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
    }));
  };
  
  const availableParents = useMemo(() => {
    if (!unit) return allUnits;
    
    const descendants = new Set<string>();
    const findDescendants = (id: string) => {
      descendants.add(id);
      allUnits.filter(u => u.parentId === id).forEach(child => findDescendants(child.id));
    };
    findDescendants(unit.id);
    
    return allUnits.filter(u => !descendants.has(u.id));
  }, [unit, allUnits]);

  // If the current parentId isn't a valid option (e.g. was set to a now-deleted
  // or self-referential node), auto-reset to root so it doesn't get silently re-saved.
  const parentIdValid =
    !formData.parentId ||
    availableParents.some(u => u.id === formData.parentId);
  
  return (
    <AdminModalPortal>
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-[var(--rule)] dark:border-[var(--rule)]">
        <div className="p-6 border-b border-[var(--rule)] dark:border-[var(--rule)]">
          <h2 className="text-xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            {isEditing ? 'Edit Organizational Unit' : 'Add Organizational Unit'}
          </h2>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
              Display Name <span className="text-[var(--coral)]">*</span>
            </label>
            <input
              type="text"
              value={formData.displayName}
              onChange={(e) => handleDisplayNameChange(e.target.value)}
              placeholder="e.g., Engineering Team"
              className="w-full px-3 py-2 border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 dark:placeholder-[var(--text-ter)] focus:ring-2 focus:ring-primary-500 focus:outline-none"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
              Internal Name <span className="text-[var(--coral)]">*</span>
              <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)] font-normal ml-1">(slug)</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., engineering-team"
              className="w-full px-3 py-2 border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 dark:placeholder-[var(--text-ter)] focus:ring-2 focus:ring-primary-500 focus:outline-none font-mono text-sm"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
              Type
            </label>
            <select
              value={formData.type ?? 'team'}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as OrgUnitType }))}
              className="w-full px-3 py-2 border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:ring-2 focus:ring-primary-500 focus:outline-none"
            >
              <option value="organization">Organization</option>
              <option value="division">Division</option>
              <option value="department">Department</option>
              <option value="team">Team</option>
              <option value="workgroup">Workgroup</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
              Parent Unit
            </label>
            {!parentIdValid && (
              <p className="mb-1 text-xs text-[var(--mustard)] dark:text-[var(--mustard)] bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border border-[var(--mustard)] dark:border-[var(--mustard)] rounded px-2 py-1">
                ⚠ The saved parent is no longer valid and has been reset to root. Save to confirm.
              </p>
            )}
            <select
              value={parentIdValid ? (formData.parentId || '') : ''}
              onChange={(e) => setFormData(prev => ({ ...prev, parentId: e.target.value || undefined }))}
              className="w-full px-3 py-2 border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:ring-2 focus:ring-primary-500 focus:outline-none"
            >
              <option value="">None (Root Level)</option>
              {availableParents.map(u => (
                <option key={u.id} value={u.id}>
                  {'  '.repeat(u.depth)}{u.displayName}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of this unit's purpose"
              rows={2}
              className="w-full px-3 py-2 border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 dark:placeholder-[var(--text-ter)] focus:ring-2 focus:ring-primary-500 focus:outline-none"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="active"
              checked={formData.active}
              onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
              className="rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="active" className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
              Active
            </label>
          </div>
        </div>
        
        <div className="p-6 border-t border-[var(--rule)] dark:border-[var(--rule)] flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(formData)}
            disabled={!formData.displayName?.trim() || !formData.name?.trim()}
            className="px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isEditing ? 'Save Changes' : 'Add Unit'}
          </button>
        </div>
      </div>
    </div>
    </AdminModalPortal>
  );
}
